import { RenderRTE } from '@/components-2.0/structure/RenderRTE';
import { ContentCardImageSide, ContentCardTheme } from '@/ui-components/molecules/ContentCard/ContentCard.type';
import { StandardVideo } from '@/ui-components/molecules/Media/StandardVideo/StandardVideo';
import { Video as InlineVideo } from '@/ui-components/molecules/Media/Video/Video';
import ImageOptimization from '@/services/image_optimization';

const SQUARED_POSTER_SIZE = { width: 540, height: 304 };
const DEFAULT_POSTER_SIZE = { width: 350, height: 350 };

const FEATURED_CARD_MAX_WIDTH = 600;

export function resolveMaxWidthTier(theme: ContentCardTheme, cardCount: number): number {
    if (theme === 'featuredcard') {
        return FEATURED_CARD_MAX_WIDTH;
    }

    if (cardCount > 3) {
        return 300;
    }

    if (cardCount > 2) {
        return 400;
    }

    return FEATURED_CARD_MAX_WIDTH;
}

export function resolveImageSide(
    layout: string | undefined,
    imageAlign: string | undefined,
    index: number,
): ContentCardImageSide | undefined {
    const alignment = imageAlign?.toLowerCase() || 'left';
    const isEvenCard = index % 2 === 0;

    const sideByLayoutAndAlignment: { [layoutAndAlignment: string]: string } = {
        Pingpong_left: isEvenCard ? 'left' : 'right',
        Pingpong_right: isEvenCard ? 'right' : 'left',
        _default: alignment,
    };

    const key = layout ? `${layout}_${alignment}` : '';
    const imageSide = sideByLayoutAndAlignment[key] ?? sideByLayoutAndAlignment._default;

    return isContentCardImageSide(imageSide) ? imageSide : undefined;
}

export function resolveContentCardMedia(
    video: SiteVideo | undefined,
    isModalVideo: boolean,
): React.JSX.Element | undefined {
    if (!video?.fields) {
        return undefined;
    }

    const imageSizing = video.fields.image_style_squared ? SQUARED_POSTER_SIZE : DEFAULT_POSTER_SIZE;
    const thumbnail = video.fields.video_thumbnail_image;

    const poster =
        thumbnail?.url &&
        ImageOptimization({
            src: thumbnail.url,
            width: thumbnail.width || imageSizing.width,
            height: thumbnail.height || imageSizing.height,
        });

    if (isModalVideo && video.fields.youtube?.fields?.media_id) {
        return <StandardVideo absoluteCentered poster={poster} videoId={video.fields.youtube.fields.media_id} />;
    }

    if (video.fields.aem_video?.url) {
        return (
            <InlineVideo
                poster={poster}
                fullSizePoster
                source={video.fields.aem_video.url}
                altText={video.fields.aem_video.altText}
                transcript={<RenderRTE contentfulDoc={video.fields.transcript} />}
                absoluteCentered
                isModal={isModalVideo}
                captionSrc={video.fields.subtitles?.url}
            />
        );
    }

    return undefined;
}

function isContentCardImageSide(possibleImageSide: unknown): possibleImageSide is ContentCardImageSide {
    return possibleImageSide === 'left' || possibleImageSide === 'right';
}
